import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Paper,
  Grid,
  CardContent,
  Avatar,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Divider,
  alpha,
  useTheme,
  Tabs,
  Tab,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
// Using new design system components
import { Button, Typography, Card } from '../../../design-system';
import { DESIGN_TOKENS } from '../../../design-system/tokens/colors';
import {
  Receipt,
  CheckCircle,
  Warning,
  Schedule,
  MoreVert,
  Visibility,
  GetApp,
  Send,
  Edit,
  Delete,
  Add,
  FilterList,
} from '@mui/icons-material';

interface InvoiceManagementProps {
  tenantId?: string;
  farmId?: string;
}

interface Invoice {
  id: string;
  invoice_number: string;
  invoice_type: 'receivable' | 'payable';
  business_contact_name: string;
  issue_date: string;
  due_date: string;
  total_amount: number;
  status: 'draft' | 'sent' | 'paid' | 'overdue' | 'cancelled';
  description: string;
  payment_terms: string;
}

// Mock invoice data
const mockInvoices: Invoice[] = [
  {
    id: 'inv-001',
    invoice_number: 'FAT-2024-001',
    invoice_type: 'receivable',
    business_contact_name: 'Laticínio Vale Verde',
    issue_date: '2024-01-20',
    due_date: '2024-01-27',
    total_amount: 15000,
    status: 'paid',
    description: 'Venda de leite - 5.000 litros',
    payment_terms: '7 dias'
  },
  {
    id: 'inv-002',
    invoice_number: 'FAT-2024-002',
    invoice_type: 'receivable',
    business_contact_name: 'Frigorífico Regional',
    issue_date: '2024-01-22',
    due_date: '2024-02-06',
    total_amount: 12000,
    status: 'sent',
    description: 'Venda de gado - 3 cabeças',
    payment_terms: '15 dias'
  },
  {
    id: 'inv-003',
    invoice_number: 'COMP-2024-005',
    invoice_type: 'payable',
    business_contact_name: 'Agrocampo Nutrição',
    issue_date: '2024-01-18',
    due_date: '2024-02-17',
    total_amount: 4625,
    status: 'paid',
    description: 'Ração Premium 18% - 2.500 kg',
    payment_terms: '30 dias'
  },
  {
    id: 'inv-004',
    invoice_number: 'COMP-2024-006',
    invoice_type: 'payable',
    business_contact_name: 'Veterinária Rural Dr. Ana',
    issue_date: '2024-01-25',
    due_date: '2024-02-09',
    total_amount: 812.50,
    status: 'overdue',
    description: 'Consulta veterinária e vacinação',
    payment_terms: '15 dias'
  },
  {
    id: 'inv-005',
    invoice_number: 'FAT-2024-003',
    invoice_type: 'receivable',
    business_contact_name: 'Cooperativa Agropecuária',
    issue_date: '2024-01-26',
    due_date: '2024-02-10',
    total_amount: 8500,
    status: 'sent',
    description: 'Venda de milho - 500 sacas',
    payment_terms: '15 dias'
  },
  {
    id: 'inv-006',
    invoice_number: 'COMP-2024-007',
    invoice_type: 'payable',
    business_contact_name: 'Posto de Combustível',
    issue_date: '2024-01-28',
    due_date: '2024-02-12',
    total_amount: 1850,
    status: 'sent',
    description: 'Combustível e lubrificantes',
    payment_terms: '15 dias'
  }
];

export const InvoiceManagement: React.FC<InvoiceManagementProps> = ({
  tenantId,
  farmId,
}) => {
  const { t } = useTranslation('farm');
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);

  useEffect(() => {
    // Simulate loading
    const timer = setTimeout(() => {
      setLoading(false);
    }, 800);
    return () => clearTimeout(timer);
  }, []);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      draft: DESIGN_TOKENS.colors.text.secondary,
      sent: DESIGN_TOKENS.colors.status.warning[500],
      paid: DESIGN_TOKENS.colors.status.success[500],
      overdue: DESIGN_TOKENS.colors.status.error[500],
      cancelled: DESIGN_TOKENS.colors.text.disabled,
    };
    return colors[status] || DESIGN_TOKENS.colors.text.secondary;
  };

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      draft: t('invoice_management.status.draft'),
      sent: t('invoice_management.status.sent'),
      paid: t('invoice_management.status.paid'),
      overdue: t('invoice_management.status.overdue'),
      cancelled: t('invoice_management.status.cancelled'),
    };
    return labels[status] || status;
  };

  const getStatusIcon = (status: string) => {
    const icons: Record<string, React.ReactElement> = {
      draft: <Edit />,
      sent: <Send />,
      paid: <CheckCircle />,
      overdue: <Warning />,
      cancelled: <Delete />,
    };
    return icons[status] || <Receipt />;
  };

  const filterInvoices = (type?: 'receivable' | 'payable', status?: string) => {
    let filtered = mockInvoices;
    
    if (type) {
      filtered = filtered.filter(inv => inv.invoice_type === type);
    }
    
    if (status) {
      filtered = filtered.filter(inv => inv.status === status);
    }
    
    return filtered;
  };

  const getTabInvoices = () => {
    switch (activeTab) {
      case 0: return mockInvoices; // All
      case 1: return filterInvoices('receivable'); // Receivable
      case 2: return filterInvoices('payable'); // Payable
      case 3: return filterInvoices(undefined, 'overdue'); // Overdue
      default: return mockInvoices;
    }
  };

  const getSummaryData = () => {
    const receivables = filterInvoices('receivable');
    const payables = filterInvoices('payable');
    const overdue = filterInvoices(undefined, 'overdue');
    
    return {
      total_receivables: receivables.reduce((sum, inv) => sum + inv.total_amount, 0),
      total_payables: payables.reduce((sum, inv) => sum + inv.total_amount, 0),
      overdue_amount: overdue.reduce((sum, inv) => sum + inv.total_amount, 0),
      total_invoices: mockInvoices.length,
    };
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, invoice: Invoice) => {
    setAnchorEl(event.currentTarget);
    setSelectedInvoice(invoice);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedInvoice(null);
  };

  const handleViewInvoice = () => {
    setViewDialogOpen(true);
    handleMenuClose();
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
        <Typography variant="body2" sx={{ mt: 2, textAlign: 'center' }}>
          {t('invoice_management.loading')}
        </Typography>
      </Box>
    );
  }

  const summaryData = getSummaryData();
  const tabInvoices = getTabInvoices();

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Avatar sx={{ bgcolor: DESIGN_TOKENS.colors.organization.farm, width: 48, height: 48 }}>
            <Receipt />
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: DESIGN_TOKENS.colors.organization.farm, mb: 0.5 }}>
              {t('invoice_management.title')}
            </Typography>
            <Typography variant="body1" color="secondary">
              {t('invoice_management.description')}
            </Typography>
          </Box>
          <Button
            variant="primary"
            startIcon={<Add />}
            sx={{ 
              bgcolor: DESIGN_TOKENS.colors.organization.farm, 
              '&:hover': { bgcolor: DESIGN_TOKENS.colors.primary[600] },
              borderRadius: 2,
              px: 3
            }}
          >
            {t('invoice_management.new_invoice')}
          </Button>
        </Box>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ height: '100%', border: '2px solid transparent', '&:hover': { borderColor: DESIGN_TOKENS.colors.status.success[500] } }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar sx={{ bgcolor: alpha(DESIGN_TOKENS.colors.status.success[500], 0.1), color: DESIGN_TOKENS.colors.status.success[500], mr: 2 }}>
                  <CheckCircle />
                </Avatar>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: DESIGN_TOKENS.colors.status.success[500] }}>
                    {formatCurrency(summaryData.total_receivables)}
                  </Typography>
                  <Typography variant="body2" color="secondary">
                    {t('invoice_management.receivable')}
                  </Typography>
                </Box>
              </Box>
              <Typography variant="caption" sx={{ color: DESIGN_TOKENS.colors.status.success[500], fontWeight: 600 }}>
                {filterInvoices('receivable').length} {t('invoice_management.invoices')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ height: '100%', border: '2px solid transparent', '&:hover': { borderColor: DESIGN_TOKENS.colors.status.warning[500] } }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar sx={{ bgcolor: alpha(DESIGN_TOKENS.colors.status.warning[500], 0.1), color: DESIGN_TOKENS.colors.status.warning[500], mr: 2 }}>
                  <Schedule />
                </Avatar>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: DESIGN_TOKENS.colors.status.warning[500] }}>
                    {formatCurrency(summaryData.total_payables)}
                  </Typography>
                  <Typography variant="body2" color="secondary">
                    {t('invoice_management.payable')}
                  </Typography>
                </Box>
              </Box>
              <Typography variant="caption" sx={{ color: DESIGN_TOKENS.colors.status.warning[500], fontWeight: 600 }}>
                {filterInvoices('payable').length} {t('invoice_management.invoices')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ height: '100%', border: '2px solid transparent', '&:hover': { borderColor: DESIGN_TOKENS.colors.status.error[500] } }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar sx={{ bgcolor: alpha(DESIGN_TOKENS.colors.status.error[500], 0.1), color: DESIGN_TOKENS.colors.status.error[500], mr: 2 }}>
                  <Warning />
                </Avatar>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: DESIGN_TOKENS.colors.status.error[500] }}>
                    {formatCurrency(summaryData.overdue_amount)}
                  </Typography>
                  <Typography variant="body2" color="secondary">
                    {t('invoice_management.overdue')}
                  </Typography>
                </Box>
              </Box>
              <Typography variant="caption" sx={{ color: DESIGN_TOKENS.colors.status.error[500], fontWeight: 600 }}>
                {filterInvoices(undefined, 'overdue').length} {t('invoice_management.invoices')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ height: '100%', border: '2px solid transparent', '&:hover': { borderColor: DESIGN_TOKENS.colors.organization.farm } }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar sx={{ bgcolor: alpha(DESIGN_TOKENS.colors.organization.farm, 0.1), color: DESIGN_TOKENS.colors.organization.farm, mr: 2 }}>
                  <Receipt />
                </Avatar>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: DESIGN_TOKENS.colors.organization.farm }}>
                    {summaryData.total_invoices}
                  </Typography>
                  <Typography variant="body2" color="secondary">
                    {t('invoice_management.total_invoices')}
                  </Typography>
                </Box>
              </Box>
              <Typography variant="caption" sx={{ color: DESIGN_TOKENS.colors.organization.farm, fontWeight: 600 }}>
                {t('invoice_management.this_month')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Invoice List */}
      <Paper sx={{ p: 3 }}>
        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs 
            value={activeTab} 
            onChange={(_, newValue) => setActiveTab(newValue)}
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                minWidth: 120,
              },
              '& .Mui-selected': {
                color: DESIGN_TOKENS.colors.organization.farm,
              },
              '& .MuiTabs-indicator': {
                backgroundColor: DESIGN_TOKENS.colors.organization.farm,
              },
            }}
          >
            <Tab label={t('invoice_management.all')} />
            <Tab label={t('invoice_management.receivable')} />
            <Tab label={t('invoice_management.payable')} />
            <Tab label={t('invoice_management.overdue')} />
          </Tabs>
        </Box>

        {/* Actions Bar */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {tabInvoices.length} {t('invoice_management.invoices_found')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="secondary"
              startIcon={<FilterList />}
              size="small"
              sx={{ borderColor: DESIGN_TOKENS.colors.organization.farm, color: DESIGN_TOKENS.colors.organization.farm }}
            >
              {t('invoice_management.filters')}
            </Button>
            <Button
              variant="secondary"
              startIcon={<GetApp />}
              size="small"
              sx={{ borderColor: DESIGN_TOKENS.colors.organization.farm, color: DESIGN_TOKENS.colors.organization.farm }}
            >
              {t('invoice_management.export')}
            </Button>
          </Box>
        </Box>

        {/* Invoice Table */}
        <TableContainer>
          <Table>
            <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>{t('invoice_management.table.number')}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t('invoice_management.table.type')}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t('invoice_management.table.client_supplier')}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t('invoice_management.table.description')}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t('invoice_management.table.issue_date')}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t('invoice_management.table.due_date')}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t('invoice_management.table.status')}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t('invoice_management.table.value')}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t('invoice_management.table.actions')}</TableCell>
                </TableRow>
              </TableHead>
            <TableBody>
              {tabInvoices.map((invoice) => (
                <TableRow 
                  key={invoice.id} 
                  hover 
                  sx={{ '&:hover': { backgroundColor: alpha(DESIGN_TOKENS.colors.organization.farm, 0.05) } }}
                >
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: DESIGN_TOKENS.colors.organization.farm }}>
                      {invoice.invoice_number}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={invoice.invoice_type === 'receivable' ? 'A Receber' : 'A Pagar'}
                      size="small"
                      variant="outlined"
                      sx={{
                        borderColor: invoice.invoice_type === 'receivable' ? DESIGN_TOKENS.colors.status.success[500] : DESIGN_TOKENS.colors.status.warning[500],
                        color: invoice.invoice_type === 'receivable' ? DESIGN_TOKENS.colors.status.success[500] : DESIGN_TOKENS.colors.status.warning[500],
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {invoice.business_contact_name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="secondary">
                      {invoice.description}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(invoice.issue_date)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(invoice.due_date)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={getStatusIcon(invoice.status)}
                      label={getStatusLabel(invoice.status)}
                      size="small"
                      sx={{
                        bgcolor: alpha(getStatusColor(invoice.status), 0.1),
                        color: getStatusColor(invoice.status),
                        fontWeight: 600,
                        '& .MuiChip-icon': {
                          color: getStatusColor(invoice.status),
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 700,
                        color: invoice.invoice_type === 'receivable' ? DESIGN_TOKENS.colors.status.success[500] : DESIGN_TOKENS.colors.status.warning[500],
                        fontSize: '1rem'
                      }}
                    >
                      {formatCurrency(invoice.total_amount)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuClick(e, invoice)}
                      sx={{ color: DESIGN_TOKENS.colors.organization.farm }}
                    >
                      <MoreVert />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={handleViewInvoice}>
          <Visibility sx={{ mr: 1 }} />
          {t('invoice_management.actions.view')}
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>
          <Edit sx={{ mr: 1 }} />
          {t('invoice_management.actions.edit')}
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>
          <GetApp sx={{ mr: 1 }} />
          {t('invoice_management.actions.download')}
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleMenuClose} sx={{ color: 'error.main' }}>
          <Delete sx={{ mr: 1 }} />
          {t('invoice_management.actions.delete')}
        </MenuItem>
      </Menu>

      {/* Invoice View Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: DESIGN_TOKENS.colors.organization.farm }}>
              <Receipt />
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {selectedInvoice?.invoice_number}
              </Typography>
              <Typography variant="caption" color="secondary">
                {selectedInvoice?.business_contact_name}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedInvoice && (
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="body2" color="secondary" gutterBottom sx={{ fontWeight: 600 }}>
                  {t('invoice_management.dialog.type')}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {selectedInvoice.invoice_type === 'receivable' ? t('invoice_management.receivable') : t('invoice_management.payable')}
                </Typography>

                <Typography variant="body2" color="secondary" gutterBottom sx={{ fontWeight: 600 }}>
                  {t('invoice_management.dialog.issue_date')}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {formatDate(selectedInvoice.issue_date)}
                </Typography>

                <Typography variant="body2" color="secondary" gutterBottom sx={{ fontWeight: 600 }}>
                  {t('invoice_management.dialog.due_date')}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {formatDate(selectedInvoice.due_date)}
                </Typography>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="body2" color="secondary" gutterBottom sx={{ fontWeight: 600 }}>
                  {t('invoice_management.dialog.status')}
                </Typography>
                <Chip
                  icon={getStatusIcon(selectedInvoice.status)}
                  label={getStatusLabel(selectedInvoice.status)}
                  sx={{
                    bgcolor: alpha(getStatusColor(selectedInvoice.status), 0.1),
                    color: getStatusColor(selectedInvoice.status),
                    fontWeight: 600,
                    mb: 2,
                  }}
                />

                <Typography variant="body2" color="secondary" gutterBottom sx={{ fontWeight: 600 }}>
                  {t('invoice_management.dialog.total_value')}
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: DESIGN_TOKENS.colors.organization.farm, mb: 2 }}>
                  {formatCurrency(selectedInvoice.total_amount)}
                </Typography>

                <Typography variant="body2" color="secondary" gutterBottom sx={{ fontWeight: 600 }}>
                  {t('invoice_management.dialog.payment_terms')}
                </Typography>
                <Typography variant="body1">
                  {selectedInvoice.payment_terms}
                </Typography>
              </Grid>

              <Grid size={12}>
                <Typography variant="body2" color="secondary" gutterBottom sx={{ fontWeight: 600 }}>
                  {t('invoice_management.dialog.description')}
                </Typography>
                <Typography variant="body1">
                  {selectedInvoice.description}
                </Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>
            {t('invoice_management.dialog.close')}
          </Button>
          <Button 
            variant="primary" 
            sx={{ bgcolor: DESIGN_TOKENS.colors.organization.farm, '&:hover': { bgcolor: DESIGN_TOKENS.colors.primary[600] } }}
          >
            {t('invoice_management.dialog.edit')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
